/**
 * Hook for managing org-level knowledge assets.
 *
 * Provides CRUD operations, file upload, URL creation,
 * and cache management via React Query.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import knowledgeAssetService, {
  detectKnowledgeAssetFileType,
  prepareKnowledgeAssetUrl,
  type KnowledgeAssetUrlType,
  type KnowledgeAssetRefreshMode,
} from '@/lib/api/knowledgeAssets';

export class KnowledgeAssetAttachedToTrainingError extends Error {
  interviewerId: string;
  interviewerName?: string;
  isPrimary: boolean;

  constructor(message: string, options: { interviewerId: string; interviewerName?: string; isPrimary?: boolean }) {
    super(message);
    this.name = 'KnowledgeAssetAttachedToTrainingError';
    this.interviewerId = options.interviewerId;
    this.interviewerName = options.interviewerName;
    this.isPrimary = Boolean(options.isPrimary);
  }
}

export function useKnowledgeAssets(organizationId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['knowledge-assets', organizationId];
  const [refreshingAssetIds, setRefreshingAssetIds] = useState<string[]>([]);

  // Fetch all assets for the org
  const {
    data: listData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await knowledgeAssetService.listAssets(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch knowledge assets');
      }
      return response.data;
    },
    enabled: !!organizationId,
    refetchInterval: (query) => query.state.data?.assets?.some(a => a.processing_status === 'processing' || a.processing_status === 'pending') ? 5000 : false,
  });

  const assets = listData?.assets ?? [];
  const error = queryError instanceof Error ? queryError.message : null;

  /**
   * Upload a file: request signed URL → upload → create asset record.
   */
  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, title }: { file: File; title?: string }) => {
      const fileType = detectKnowledgeAssetFileType(file);

      if (!fileType) {
        throw new Error('Unsupported file type. Only PDF, Markdown, text, and video files are supported.');
      }

      // 1. Request signed upload URL
      const urlResponse = await knowledgeAssetService.requestUploadUrl(
        organizationId,
        file.name,
        fileType
      );
      if (!urlResponse.success || !urlResponse.data) {
        throw new Error(urlResponse.error || 'Failed to get upload URL');
      }

      const { storage_path, upload_url } = urlResponse.data;

      // 2. Upload file to signed URL
      const uploaded = await knowledgeAssetService.uploadToSignedUrl(upload_url, file);
      if (!uploaded) {
        throw new Error('File upload failed');
      }

      // 3. Create asset record (triggers processing)
      const createResponse = await knowledgeAssetService.createFileAsset(
        organizationId,
        storage_path,
        file.name,
        fileType,
        title,
        file.size
      );
      if (!createResponse.success || !createResponse.data) {
        throw new Error(createResponse.error || 'Failed to create asset');
      }

      return createResponse.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Asset Uploaded', description: 'Processing will begin shortly.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Upload Failed', description: err.message });
    },
  });

  /**
   * Add a URL as a knowledge asset.
   */
  const addUrlMutation = useMutation({
    mutationFn: async ({ url, title, urlType }: { url: string; title?: string; urlType?: KnowledgeAssetUrlType }) => {
      const normalizedUrl = prepareKnowledgeAssetUrl(url);
      const response = await knowledgeAssetService.createUrlAsset(organizationId, normalizedUrl, title, urlType);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create URL asset');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'URL Added', description: 'Processing will begin shortly.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Failed to Add URL', description: err.message });
    },
  });

  /**
   * Update asset title or summary.
   */
  const updateMutation = useMutation({
    mutationFn: async ({ assetId, updates }: { assetId: string; updates: { title?: string; summary?: string } }) => {
      const response = await knowledgeAssetService.updateAsset(assetId, updates);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update asset');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Update Failed', description: err.message });
    },
  });

  /**
   * Delete an asset.
   */
  const deleteMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const response = await knowledgeAssetService.deleteAsset(assetId);
      if (!response.success) {
        const detail = response.details?.detail;
        if (
          response.status === 409
          && response.code === 'attached_to_training'
          && detail?.interviewer_id
        ) {
          throw new KnowledgeAssetAttachedToTrainingError(
            detail?.message || response.error || 'This source is attached to an active training workspace.',
            {
              interviewerId: detail.interviewer_id,
              interviewerName: detail.interviewer_name,
              isPrimary: detail.is_primary,
            }
          );
        }
        throw new Error(response.error || 'Failed to delete asset');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Asset Deleted' });
    },
    onError: (err: Error) => {
      if (err instanceof KnowledgeAssetAttachedToTrainingError) {
        return;
      }
      toast({ variant: 'destructive', title: 'Delete Failed', description: err.message });
    },
  });

  /**
   * Refresh a URL asset (re-extract content).
   */
  const refreshMutation = useMutation({
    mutationFn: async ({ assetId, mode }: { assetId: string; mode: KnowledgeAssetRefreshMode }) => {
      const response = await knowledgeAssetService.refreshAsset(assetId, mode);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to refresh asset');
      }
      return { asset: response.data, mode };
    },
    onSuccess: ({ asset, mode }) => {
      queryClient.invalidateQueries({ queryKey });
      if (asset.url_type === 'documentation') {
        if (mode === 'full_recrawl') {
          toast({
            title: 'Full Documentation Recrawl Started',
            description: 'We are rediscovering pages, picking up new docs, and reconciling removed pages.',
          });
          return;
        }
        toast({
          title: 'Documentation Refresh Started',
          description: 'We are recrawling known pages and rebuilding the training snapshot.',
        });
        return;
      }
      toast({ title: 'URL Refreshed', description: 'Content has been re-extracted.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Refresh Failed', description: err.message });
    },
  });

  // Track parallel uploads with a counter since useMutation.isPending
  // only reflects the latest call when invoked concurrently.
  const uploadCountRef = useRef(0);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const uploadFile = useCallback(
    async (file: File, title?: string) => {
      uploadCountRef.current++;
      setIsUploadingFiles(true);
      try {
        return await uploadFileMutation.mutateAsync({ file, title });
      } finally {
        uploadCountRef.current--;
        if (uploadCountRef.current === 0) {
          setIsUploadingFiles(false);
        }
      }
    },
    [uploadFileMutation]
  );

  const addUrl = useCallback(
    (url: string, title?: string, urlType?: KnowledgeAssetUrlType) =>
      addUrlMutation.mutateAsync({ url, title, urlType }),
    [addUrlMutation]
  );

  const updateAsset = useCallback(
    (assetId: string, updates: { title?: string; summary?: string }) =>
      updateMutation.mutateAsync({ assetId, updates }),
    [updateMutation]
  );

  const getAsset = useCallback(
    async (assetId: string) => {
      const response = await knowledgeAssetService.getAsset(assetId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load asset details');
      }
      return response.data;
    },
    []
  );

  const deleteAsset = useCallback(
    (assetId: string) => deleteMutation.mutateAsync(assetId),
    [deleteMutation]
  );

  const refreshAsset = useCallback(
    async (assetId: string, mode: KnowledgeAssetRefreshMode = 'refresh') => {
      setRefreshingAssetIds((current) => (current.includes(assetId) ? current : [...current, assetId]));
      try {
        return await refreshMutation.mutateAsync({ assetId, mode });
      } finally {
        setRefreshingAssetIds((current) => current.filter((id) => id !== assetId));
      }
    },
    [refreshMutation]
  );

  const refreshingAssetIdSet = useMemo(() => new Set(refreshingAssetIds), [refreshingAssetIds]);
  const isAssetRefreshing = useCallback(
    (assetId: string) => refreshingAssetIdSet.has(assetId),
    [refreshingAssetIdSet]
  );

  return {
    assets,
    isLoading,
    error,
    uploadFile,
    addUrl,
    getAsset,
    updateAsset,
    deleteAsset,
    refreshAsset,
    isUploading: isUploadingFiles,
    isAddingUrl: addUrlMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRefreshing: refreshingAssetIds.length > 0,
    isAssetRefreshing,
  };
}
